import type { FC, ReactNode } from "react";

import { getBuilderImageResizeUrl } from "@ui/utils/getBuilderImageResizeUrl";
import { getOptimizedBackgroundImage } from "@ui/utils/getOptimizedBackgroundImage";
import dompurify from "isomorphic-dompurify";

import { Container } from "@jti/core-components/components/Container";

import type { HeadingLevel, IconBannerCardProps } from "../IconBannerCard";
import { IconBannerCard } from "../IconBannerCard";

import styles from "./ReassuranceBanner.module.scss";

const { sanitize } = dompurify;

type Item = Required<
  Pick<IconBannerCardProps, "title" | "description"> & {
    id: string;
  }
> & {
  icon?: IconBannerCardProps["icon"];
  customIcon?: ReactNode;
  image?: IconBannerCardProps["image"];
};

export type Items = Item[];
type ReassuranceBannerProps = {
  title: string;
  items: Items;
  backgroundImageUrl?: string;
  mobileBackgroundImageUrl?: string;
  bottomText?: string;
  itemTitleHeadingLevel?: HeadingLevel;
};

const ReassuranceBanner: FC<ReassuranceBannerProps> = ({
  title,
  items,
  itemTitleHeadingLevel = "h3",
  bottomText,
  backgroundImageUrl,
  mobileBackgroundImageUrl,
}) => {
  const resizedDesktopBackgroundImage = getBuilderImageResizeUrl({ imageUrl: backgroundImageUrl });
  const resizedMobileBackgroundImage = getBuilderImageResizeUrl({
    imageUrl: mobileBackgroundImageUrl || backgroundImageUrl,
  });

  const optimizedDesktopBackgroundImage = resizedDesktopBackgroundImage
    ? getOptimizedBackgroundImage(resizedDesktopBackgroundImage)
    : "none";
  const optimizedMobileBackgroundImage = resizedMobileBackgroundImage
    ? getOptimizedBackgroundImage(resizedMobileBackgroundImage)
    : "none";

  const backgroundImages = {
    "--reassurance-banner-desktop-bg-image": optimizedDesktopBackgroundImage,
    "--reassurance-banner-mobile-bg-image": optimizedMobileBackgroundImage,
  };
  return (
    <div className={styles.wrapper} style={backgroundImages} data-testid="reassurance-banner-wrapper">
      <Container className={styles.container}>
        <div className={styles.title} dangerouslySetInnerHTML={{ __html: sanitize(title) }}></div>
        <ul className={styles.items}>
          {items.map(({ id, title, description, icon: Icon, image, customIcon }) => (
            <li className={styles.item} key={id}>
              <IconBannerCard
                itemTitleHeadingLevel={itemTitleHeadingLevel}
                title={title}
                description={description}
                icon={Icon}
                customIcon={customIcon}
                image={image}
              />
            </li>
          ))}
        </ul>
        {bottomText && <div className={styles.bottomText} dangerouslySetInnerHTML={{ __html: sanitize(bottomText) }} />}
      </Container>
    </div>
  );
};

export { ReassuranceBanner };
